import { useState, useEffect } from 'react';
import { Building2, Users, Eye, MapPin, TrendingUp, Calendar } from 'lucide-react';
import api from '../../utils/api';
import './Analytics.scss';

const Analytics = () => {
    const [year, setYear] = useState(2026);
    const [overview, setOverview] = useState(null);
    const [visitStats, setVisitStats] = useState(null);
    const [mosqueStats, setMosqueStats] = useState(null);
    const [contributorStats, setContributorStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchAnalytics();
    }, [year]);

    const fetchAnalytics = async () => {
        setLoading(true);
        try {
            const params = { year };
            const [overviewRes, visitsRes, mosquesRes, contributorsRes] = await Promise.all([
                api.get('/admin/analytics/overview', { params }),
                api.get('/admin/analytics/visits', { params }),
                api.get('/admin/analytics/mosques', { params }),
                api.get('/admin/analytics/contributors', { params })
            ]);

            if (overviewRes.data.success) setOverview(overviewRes.data.overview);
            if (visitsRes.data.success) setVisitStats(visitsRes.data.statistics);
            if (mosquesRes.data.success) setMosqueStats(mosquesRes.data.statistics);
            if (contributorsRes.data.success) setContributorStats(contributorsRes.data.statistics);
        } catch (error) {
            console.error('Error fetching analytics:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return <div className="loading">Loading analytics...</div>;
    }

    return (
        <div className="analytics">
            <div className="analytics-header">
                <h2>Analytics Dashboard</h2>
                <select
                    value={year}
                    onChange={(e) => setYear(Number(e.target.value))}
                    className="year-filter"
                    title="Filter by year"
                >
                    <option value={2026}>2026</option>
                    <option value={2025}>2025</option>
                </select>
            </div>

            {overview && (
                <div className="overview-cards">
                    <div className="stat-card">
                        <Building2 size={24} />
                        <div>
                            <h3>{overview.totalMosques}</h3>
                            <p>Total Mosques</p>
                        </div>
                    </div>

                    <div className="stat-card">
                        <Users size={24} />
                        <div>
                            <h3>{overview.totalContributors}</h3>
                            <p>Contributors</p>
                        </div>
                    </div>

                    <div className="stat-card">
                        <Eye size={24} />
                        <div>
                            <h3>{overview.totalVisits.toLocaleString()}</h3>
                            <p>Total Visits</p>
                        </div>
                    </div>

                    <div className="stat-card">
                        <MapPin size={24} />
                        <div>
                            <h3>{overview.topDistricts.length}</h3>
                            <p>Districts Covered</p>
                        </div>
                    </div>

                    <div className="stat-card">
                        <TrendingUp size={24} />
                        <div>
                            <h3>{overview.verifiedCount}</h3>
                            <p>Verified Mosques</p>
                        </div>
                    </div>

                    <div className="stat-card">
                        <Calendar size={24} />
                        <div>
                            <h3>{overview.recentActivity.mosquesAdded}</h3>
                            <p>Recent Additions (7d)</p>
                        </div>
                    </div>
                </div>
            )}

            <div className="analytics-grid">
                {mosqueStats && (
                    <div className="analytics-section">
                        <h3>Mosques by District</h3>
                        <div className="stats-list">
                            {mosqueStats.byDistrict.map((item, idx) => (
                                <div key={idx} className="stat-item">
                                    <span>{item.district}</span>
                                    <span>{item.total}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {visitStats && (
                    <div className="analytics-section">
                        <h3>Visit Statistics</h3>
                        <div className="stats-list">
                            <div className="stat-item">
                                <span>Total Visits</span>
                                <span>{visitStats.totalVisits.toLocaleString()}</span>
                            </div>
                            <div className="stat-item">
                                <span>Unique Visitors</span>
                                <span>{visitStats.uniqueVisitors.toLocaleString()}</span>
                            </div>
                        </div>
                        {visitStats.topMosques.length > 0 && (
                            <div className="top-mosques">
                                <h4>Top Visited Mosques</h4>
                                {visitStats.topMosques.slice(0, 5).map((mosque, idx) => (
                                    <div key={idx} className="stat-item">
                                        <span>{mosque.mosqueName}</span>
                                        <span>{mosque.visits}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {contributorStats && (
                    <div className="analytics-section">
                        <h3>Top Contributors</h3>
                        <div className="stats-list">
                            {contributorStats.topContributors.map((contributor, idx) => (
                                <div key={idx} className="stat-item">
                                    <div>
                                        <strong>{contributor.name}</strong>
                                        <small>{contributor.email}</small>
                                    </div>
                                    <span>{contributor.totalMosques} mosques</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Analytics;
